import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { BookOpen, Check, X, Award, ArrowRight, AlertCircle } from "lucide-react";

interface Question {
  id: string;
  text: string;
  options: string[];
  points: number;
}

interface QuizResult {
  score: number;
  results: { correct: boolean; points: number }[];
  alreadyAttempted?: boolean;
}

interface QuizModalProps {
  questions: Question[];
  correctAnswers?: number[]; // server sends after submit
  onSubmit: (answers: number[]) => Promise<QuizResult>;
  onClose: () => void;
}

export function QuizModal({ questions, onSubmit, onClose }: QuizModalProps) {
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState<(number | null)[]>(new Array(questions.length).fill(null));
  const [submitted, setSubmitted] = useState(false);
  const [results, setResults] = useState<QuizResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [alreadyDone, setAlreadyDone] = useState(false);

  // Per-question reveal state
  const [revealedQ, setRevealedQ] = useState<number | null>(null);
  const [revealResult, setRevealResult] = useState<boolean | null>(null);
  const [serverResults, setServerResults] = useState<QuizResult | null>(null);

  const selectAnswer = (optionIdx: number) => {
    if (submitted || revealedQ !== null) return;
    const newAnswers = [...answers];
    newAnswers[currentQ] = optionIdx;
    setAnswers(newAnswers);
  };

  // When user clicks "Sonraki" or "Sinavi Bitir", first reveal answer for 2.5s
  const handleNext = useCallback(() => {
    if (answers[currentQ] === null) return;

    // If we already have server results, use them for reveal
    if (serverResults) {
      const isCorrect = serverResults.results[currentQ]?.correct ?? false;
      setRevealResult(isCorrect);
      setRevealedQ(currentQ);

      setTimeout(() => {
        setRevealedQ(null);
        setRevealResult(null);
        if (currentQ < questions.length - 1) {
          setCurrentQ(c => c + 1);
        } else {
          setResults(serverResults);
          setSubmitted(true);
        }
      }, 2500);
      return;
    }

    // No server results yet — just advance
    if (currentQ < questions.length - 1) {
      setCurrentQ(c => c + 1);
    }
  }, [currentQ, answers, serverResults, questions.length]);

  const handleSubmit = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await onSubmit(answers as number[]);

      if (res.alreadyAttempted) {
        setAlreadyDone(true);
        setResults(res);
        setSubmitted(true);
        return;
      }

      // Store results, then reveal last question
      setServerResults(res);

      // Reveal current (last) question answer
      const isCorrect = res.results[currentQ]?.correct ?? false;
      setRevealResult(isCorrect);
      setRevealedQ(currentQ);

      setTimeout(() => {
        setRevealedQ(null);
        setRevealResult(null);
        setResults(res);
        setSubmitted(true);
      }, 2500);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  // Submit all answers when reaching last question to get correct answers for reveal
  useEffect(() => {
    if (currentQ === questions.length - 1 && !serverResults && !submitted && answers.filter(a => a !== null).length >= questions.length - 1) {
      // Pre-submit when on last question (will submit for real when clicking finish)
    }
  }, [currentQ, questions.length, serverResults, submitted, answers]);

  const allAnswered = answers.every(a => a !== null);
  const q = questions[currentQ];
  const totalPoints = questions.reduce((sum, q) => sum + q.points, 0);
  const isRevealing = revealedQ === currentQ;

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center">
      <div className="bg-card w-full max-w-lg rounded-t-3xl sm:rounded-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gold/10 flex items-center justify-center">
              <BookOpen size={20} className="text-gold" />
            </div>
            <div>
              <h2 className="font-bold">Haftalik Sinav</h2>
              <p className="text-xs text-muted-foreground">{questions.length} soru - {totalPoints} puan</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
            <X size={16} />
          </button>
        </div>

        {/* Already attempted */}
        {submitted && alreadyDone && results ? (
          <div className="p-6 text-center">
            <div className="w-20 h-20 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-4">
              <AlertCircle size={36} className="text-gold" />
            </div>
            <h3 className="text-xl font-bold">Bu sinavi zaten cevapladin</h3>
            <p className="text-muted-foreground text-sm mt-2">Onceki puanin:</p>
            <p className="text-3xl font-bold text-gold mt-1">{results.score} / {totalPoints}</p>
            <p className="text-muted-foreground text-sm mt-1">
              {results.results.filter(r => r.correct).length}/{questions.length} dogru cevap
            </p>
            <p className="text-xs text-muted-foreground mt-4">Her sinav sadece bir kez cevaplanabilir.</p>
            <Button onClick={onClose} size="lg" className="w-full mt-6">Tamam</Button>
          </div>
        ) : submitted && results ? (
          /* Final results */
          <div className="p-6 text-center">
            <div className="w-20 h-20 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-4">
              <Award size={36} className="text-gold" />
            </div>
            <h3 className="text-2xl font-bold">{results.score} / {totalPoints}</h3>
            <p className="text-muted-foreground text-sm mt-1">
              {results.results.filter(r => r.correct).length}/{questions.length} dogru cevap
            </p>

            <div className="mt-6 space-y-2">
              {questions.map((q, i) => (
                <div
                  key={q.id}
                  className={cn(
                    "flex items-center gap-3 p-3 rounded-xl text-left",
                    results.results[i]?.correct
                      ? "bg-ehille-green/10 border border-ehille-green/30"
                      : "bg-ehille-red/10 border border-ehille-red/30",
                  )}
                >
                  <div className={cn(
                    "w-6 h-6 rounded-full flex items-center justify-center shrink-0",
                    results.results[i]?.correct ? "bg-ehille-green text-white" : "bg-ehille-red text-white",
                  )}>
                    {results.results[i]?.correct ? <Check size={14} /> : <X size={14} />}
                  </div>
                  <span className="text-sm flex-1">{q.text}</span>
                  <Badge variant={results.results[i]?.correct ? "success" : "destructive"}>
                    {results.results[i]?.correct ? `+${q.points}` : "0"}
                  </Badge>
                </div>
              ))}
            </div>

            <Button onClick={onClose} size="lg" className="w-full mt-6">Kapat</Button>
          </div>
        ) : (
          /* Question view */
          <>
            {/* Progress dots */}
            <div className="flex items-center justify-center gap-2 py-4">
              {questions.map((_, i) => (
                <div
                  key={i}
                  className={cn(
                    "h-2.5 rounded-full transition-all",
                    i === currentQ ? "bg-gold w-6" :
                    answers[i] !== null ? "bg-gold/50 w-2.5" : "bg-muted w-2.5",
                  )}
                />
              ))}
            </div>

            <div className="px-6 pb-6">
              <div className="flex items-center justify-between mb-4">
                <Badge variant="secondary">Soru {currentQ + 1}/{questions.length}</Badge>
                <Badge variant="warning">{q.points} puan</Badge>
              </div>

              {/* Reveal feedback banner */}
              {isRevealing && revealResult !== null && (
                <div className={cn(
                  "mb-4 p-3 rounded-xl text-center font-bold text-sm animate-pulse",
                  revealResult ? "bg-ehille-green/20 text-ehille-green" : "bg-ehille-red/20 text-ehille-red",
                )}>
                  {revealResult ? "Dogru! ✓" : "Yanlis ✗"}
                </div>
              )}

              <h3 className="text-lg font-bold mb-5">{q.text}</h3>

              <div className="space-y-3">
                {q.options.map((opt, i) => {
                  const isSelected = answers[currentQ] === i;
                  const showCorrect = isRevealing && revealResult === true && isSelected;
                  const showWrong = isRevealing && revealResult === false && isSelected;

                  return (
                    <button
                      key={i}
                      onClick={() => selectAnswer(i)}
                      disabled={isRevealing}
                      className={cn(
                        "w-full p-4 rounded-xl border-2 text-left text-sm font-medium transition-all",
                        showCorrect
                          ? "border-ehille-green bg-ehille-green/15 scale-[1.02]"
                          : showWrong
                            ? "border-ehille-red bg-ehille-red/15 shake"
                            : isSelected
                              ? "border-gold bg-gold/10 text-foreground"
                              : "border-border bg-card hover:border-muted-foreground/30",
                        isRevealing && "pointer-events-none",
                        !isRevealing && "active:scale-[0.98]",
                      )}
                    >
                      <span className="flex items-center gap-3">
                        <span className={cn(
                          "w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0",
                          showCorrect ? "bg-ehille-green text-white" :
                          showWrong ? "bg-ehille-red text-white" :
                          isSelected ? "bg-gold text-ehille-dark" :
                          "bg-muted text-muted-foreground",
                        )}>
                          {showCorrect ? <Check size={14} /> :
                           showWrong ? <X size={14} /> :
                           String.fromCharCode(65 + i)}
                        </span>
                        {opt}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Navigation */}
              {!isRevealing && (
                <div className="flex gap-3 mt-6">
                  {currentQ > 0 && (
                    <Button variant="outline" className="flex-1" onClick={() => setCurrentQ(c => c - 1)}>
                      Onceki
                    </Button>
                  )}
                  {currentQ < questions.length - 1 ? (
                    <Button
                      className="flex-1 gap-2"
                      onClick={handleNext}
                      disabled={answers[currentQ] === null}
                    >
                      Sonraki <ArrowRight size={16} />
                    </Button>
                  ) : (
                    <Button
                      className="flex-1"
                      onClick={handleSubmit}
                      disabled={!allAnswered || loading}
                    >
                      {loading ? "Gonderiliyor..." : "Sinavi Bitir"}
                    </Button>
                  )}
                </div>
              )}

              {/* Reveal: auto-advancing indicator */}
              {isRevealing && (
                <div className="mt-6 text-center">
                  <div className="w-full h-1 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-gold rounded-full animate-[shrink_2.5s_linear]" style={{ width: "100%" }} />
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-2">Sonraki soruya geciliyor...</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
